import { STALL_STAGE_LABELS } from '../../shared/stall.js';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiRequest, apiDownload } from '../api/client.js';
import type { FacilityOption, WorkOrderListResponse } from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import {
  PRIMARY_WORK_ORDER_STATUSES,
  SECONDARY_WORK_ORDER_STATUSES,
  WORK_ORDER_STATUS_LABELS,
  PRIORITIES,
  PRIORITY_LABELS,
} from '../../shared/workflow.js';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  PriorityBadge,
  Select,
  Spinner,
  StatusBadge,
  TextInput,
} from '../components/ui.js';
import { formatCurrency, formatDate } from '../lib/format.js';

const PAGE_SIZE = 25;

export function WorkOrderListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { can, isContractor } = useSession();
  const [searchDraft, setSearchDraft] = useState(searchParams.get('search') ?? '');
  const [downloading, setDownloading] = useState(false);

  const page = Number(searchParams.get('page') ?? '1');
  const offset = (Math.max(page, 1) - 1) * PAGE_SIZE;

  const { data: facilities } = useQuery({
    queryKey: ['reference', 'facilities'],
    queryFn: () =>
      apiRequest<{ facilities: FacilityOption[] }>('/api/reference/facilities').then(
        (result) => result.facilities,
      ),
  });

  const filters = useMemo(
    () => ({
      status: searchParams.get('status') ?? undefined,
      priority: searchParams.get('priority') ?? undefined,
      facilityId: searchParams.get('facilityId') ?? undefined,
      search: searchParams.get('search') ?? undefined,
      view: searchParams.get('view') ?? undefined,
      sort: searchParams.get('sort') ?? 'submitted_at',
      direction: searchParams.get('direction') ?? 'desc',
    }),
    [searchParams],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['work-orders', filters, offset],
    queryFn: () =>
      apiRequest<WorkOrderListResponse>('/api/work-orders', {
        query: { ...filters, limit: PAGE_SIZE, offset },
      }),
  });

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setSearchParams(next);
  };

  const handleExport = async () => {
    setDownloading(true);
    try {
      await apiDownload('/api/reports/work-orders', 'work-orders.csv', {
        ...filters,
        format: 'csv',
      });
    } finally {
      setDownloading(false);
    }
  };

  const total = data?.total ?? 0;
  const pageCount = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  return (
    <>
      <PageHeader
        title="Work orders"
        subtitle={total > 0 ? `${total} matching work order${total === 1 ? '' : 's'}` : undefined}
        actions={
          <>
            {can(PERMISSIONS.REPORT_VIEW) && (
              <Button variant="secondary" onClick={() => void handleExport()} loading={downloading}>
                Export CSV
              </Button>
            )}
            {can(PERMISSIONS.WORK_ORDER_CREATE) && (
              <Link to="/work-orders/new">
                <Button>New work order</Button>
              </Link>
            )}
          </>
        }
      />

      <Card className="mb-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Search" htmlFor="search" className="lg:col-span-2">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                updateFilter('search', searchDraft.trim());
              }}
              className="flex gap-2"
            >
              <TextInput
                id="search"
                type="search"
                placeholder="Number, title, equipment, location"
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
              />
              <Button type="submit" variant="secondary">
                Search
              </Button>
            </form>
          </Field>

          <Field label="Status" htmlFor="status">
            <Select
              id="status"
              value={filters.status ?? ''}
              onChange={(event) => updateFilter('status', event.target.value)}
            >
              <option value="">All statuses</option>
              <optgroup label="Workflow">
                {PRIMARY_WORK_ORDER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {WORK_ORDER_STATUS_LABELS[status]}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Other">
                {SECONDARY_WORK_ORDER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {WORK_ORDER_STATUS_LABELS[status]}
                  </option>
                ))}
              </optgroup>
            </Select>
          </Field>

          <Field label="Priority" htmlFor="priority">
            <Select
              id="priority"
              value={filters.priority ?? ''}
              onChange={(event) => updateFilter('priority', event.target.value)}
            >
              <option value="">All priorities</option>
              {PRIORITIES.map((priority) => (
                <option key={priority} value={priority}>
                  {PRIORITY_LABELS[priority]}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Facility" htmlFor="facility">
            <Select
              id="facility"
              value={filters.facilityId ?? ''}
              onChange={(event) => updateFilter('facilityId', event.target.value)}
            >
              <option value="">All facilities</option>
              {(facilities ?? []).map((facility) => (
                <option key={facility.id} value={facility.id}>
                  {facility.facilityCode} — {facility.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="View" htmlFor="view">
            <Select
              id="view"
              value={filters.view ?? ''}
              onChange={(event) => updateFilter('view', event.target.value)}
            >
              <option value="">All work orders</option>
              <option value="open">Open only</option>
              <option value="overdue">Past requested date</option>
              {isContractor && <option value="assigned_to_me">Assigned to me</option>}
              <option value="submitted_by_me">Submitted by me</option>
            </Select>
          </Field>

          <div className="flex items-end">
            <Button
              variant="ghost"
              onClick={() => {
                setSearchDraft('');
                setSearchParams(new URLSearchParams());
              }}
            >
              Clear filters
            </Button>
          </div>
        </div>
      </Card>

      {error ? (
        <Alert variant="error">
          The work order list could not be loaded. Please refresh the page.
        </Alert>
      ) : isLoading ? (
        <Spinner label="Loading work orders" />
      ) : (data?.items.length ?? 0) === 0 ? (
        <Card>
          <EmptyState
            title="No work orders match these filters"
            description="Try clearing a filter or widening your search."
          />
        </Card>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg bg-white shadow-sm ring-1 ring-slate-200">
            <table className="min-w-full divide-y divide-slate-200">
              <caption className="sr-only">Work orders matching the current filters</caption>
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className="table-header-cell">
                    Number
                  </th>
                  <th scope="col" className="table-header-cell">
                    Title
                  </th>
                  <th scope="col" className="table-header-cell">
                    Facility
                  </th>
                  <th scope="col" className="table-header-cell">
                    Status
                  </th>
                  <th scope="col" className="table-header-cell">
                    Priority
                  </th>
                  <th scope="col" className="table-header-cell">
                    Submitted
                  </th>
                  {isContractor && (
                    <th scope="col" className="table-header-cell">
                      Assigned to
                    </th>
                  )}
                  <th scope="col" className="table-header-cell text-right">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {data?.items.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="table-cell-base">
                      <Link
                        to={`/work-orders/${item.id}`}
                        className="font-mono text-xs font-semibold text-brand-800 hover:underline"
                      >
                        {item.workOrderNumber}
                      </Link>
                    </td>
                    <td className="max-w-xs truncate px-3 py-2.5 text-sm text-slate-900">
                      <Link to={`/work-orders/${item.id}`} className="hover:underline">
                        {item.title}
                      </Link>
                    </td>
                    <td className="table-cell-base">{item.facilityCode}</td>
                    <td className="table-cell-base">
                      <div className="flex flex-col items-start gap-1">
                        <StatusBadge status={item.status} kind={item.kind} />
                        {item.stalledStage && (
                          <span className="inline-flex items-center whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-800 ring-1 ring-inset ring-red-300">
                            {STALL_STAGE_LABELS[item.stalledStage]}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="table-cell-base">
                      <PriorityBadge priority={item.priority} />
                    </td>
                    <td className="table-cell-base">{formatDate(item.submittedAt)}</td>
                    {isContractor && (
                      <td className="table-cell-base">{item.assignedToName ?? '—'}</td>
                    )}
                    <td className="table-cell-base text-right tabular-nums">
                      {item.approvedTotalCents !== null
                        ? formatCurrency(item.approvedTotalCents)
                        : item.pendingProposalTotalCents !== null
                          ? `${formatCurrency(item.pendingProposalTotalCents)} pending`
                          : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <nav className="mt-4 flex items-center justify-between" aria-label="Pagination">
              <p className="text-sm text-slate-600">
                Page {page} of {pageCount}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.set('page', String(page - 1));
                    setSearchParams(next);
                  }}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  disabled={page >= pageCount}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.set('page', String(page + 1));
                    setSearchParams(next);
                  }}
                >
                  Next
                </Button>
              </div>
            </nav>
          )}
        </>
      )}
    </>
  );
}
